<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col class="d-flex align-center">
          <v-breadcrumbs :items="breadcrumbs" class="px-0 py-0 mr-4" />
          <flag-pip
            v-if="formData.language && formData.language.flag"
            :flag="formData.language.flag"
            :text="formData.language.name"
            :show-tooltip="true"
          />
        </v-col>
        <v-col class="d-flex align-center justify-end">
          <v-tooltip max-width="300" bottom>
            <template #activator="{ on }">
              <base-chip v-show="hasChanges" color="blue" text="Changes Detected" icon="info" dark small v-on="on">
              </base-chip>
            </template>
            <span>Click the FAB in the bottom right then click "Save Buy Guide" to keep your changes!</span>
          </v-tooltip>
          <div class="text-caption font-italic ml-4">
            {{ formatLastUpdated(original.updated_at) }}
          </div>
        </v-col>
      </v-row>
    </v-container>
    <v-divider class="my-2" />

    <v-form ref="form">
      <v-container>
        <v-row>
          <v-col class="order-sm-2" sm="4">
            <v-card outlined>
              <v-card-subtitle class="mb-4 grey lighten-4 pa-3">Template Details</v-card-subtitle>
              <v-card-text>
                <select-list
                  v-model="formData.type"
                  :items="getItemsForOption('buyGuideTypes')"
                  class="mb-6"
                  label="Type"
                  disabled
                  hide-details
                  required
                />
                <select-list
                  v-model="formData.language_id"
                  :items="languageOptions"
                  class="mb-6"
                  label="Language"
                  disabled
                  hide-details
                  required
                />
                <select-list
                  v-if="!formData.is_default"
                  v-model="formData.entity_id"
                  :items="entities"
                  class="mb-6"
                  label="Entity"
                  disabled
                  hide-details
                  required
                />
                <select-list
                  v-if="formData.is_default"
                  v-model="formData.entity_type_id"
                  :items="entityTypes"
                  class="mb-6"
                  label="Entity Type"
                  disabled
                  hide-details
                />
                <toggle-input v-model="formData.is_default" label="Is Default" is-disabled />
              </v-card-text>
            </v-card>
            <v-card class="mt-6" outlined>
              <v-card-subtitle class="grey lighten-4 pa-3">Languages</v-card-subtitle>
              <v-list class="overflow-auto py-0" max-height="500">
                <v-list-item
                  v-for="guide in guideLanguages"
                  :key="`${guide.language.name}-${guide.id}`"
                  :disabled="guide.disabled"
                  :inactive="guide.disabled"
                  :to="{ name: 'content.buy-guides.edit', params: { id: guide.id } }"
                >
                  <v-list-item-content>
                    <v-list-item-title class="d-flex align-center">
                      <base-chip
                        :color="guide.id ? 'success' : 'error'"
                        :icon="guide.id ? 'check' : 'close'"
                        label
                        small
                        class="px-1 mr-2 flex-shrink-0"
                      />
                      <flag-pip :flag="guide.language.flag" :text="guide.language.name" class="mx-2" />
                      <v-list-item-subtitle>{{ guide.language.name }}</v-list-item-subtitle>
                      <v-spacer />
                      <template v-if="isCurrentGuideEnglish && guide.language.code !== 'en'">
                        <v-tooltip top>
                          <template #activator="{ on }">
                            <div v-on="hasChanges ? on : null">
                              <v-btn
                                :disabled="hasChanges"
                                class="pointer"
                                color="secondary"
                                depressed
                                small
                                @click.prevent="onTranslateButtonClick(guide)"
                              >
                                <span>Translate</span>
                              </v-btn>
                            </div>
                          </template>
                          <span>Changes must be saved before translating</span>
                        </v-tooltip>
                      </template>
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
              <v-card-actions class="d-block">
                <v-divider class="mb-2" />
                <template v-if="isCurrentGuideEnglish">
                  <v-tooltip top>
                    <template #activator="{ on }">
                      <div v-on="hasChanges ? on : null">
                        <v-btn
                          :disabled="hasChanges"
                          color="secondary"
                          depressed
                          small
                          @click="showTranslateAllModal = true"
                        >
                          <span>Translate All</span>
                        </v-btn>
                      </div>
                    </template>
                    <span>Changes must be saved before translating</span>
                  </v-tooltip>
                </template>
                <template v-else>
                  <v-icon small left>info</v-icon>
                  <span class="grey--text text--darken-1" style="font-size: 12px"
                    >Translations can <b>only</b> be performed from English Buy Guides</span
                  >
                </template>
              </v-card-actions>
            </v-card>
          </v-col>
          <v-col class="order-sm-1" sm="8">
            <category-options
              v-for="(fields, category) in schema.general"
              :key="category"
              :category="category"
              :data="formData"
              :fields="fields"
              :is-disabled="!isPermittedTo('update')"
              :show-category="false"
              @input="onInput"
            />
          </v-col>
        </v-row>
      </v-container>
    </v-form>

    <fab-actions
      :can-delete="canDelete"
      :can-edit="isPermittedTo('update')"
      :has-changes="hasChanges"
      context="Buy Guide"
      @delete="showDeleteModal = true"
      @reset="showResetModal = true"
      @save="showSaveModal = true"
      @help="showHelpModal = true"
    />

    <delete-modal
      v-model="showDeleteModal"
      :is-disabled="loading"
      :is-loading="loading"
      :item="item.name"
      category="Entity"
      @modal-confirm="onDeleteConfirm"
    />

    <reset-modal
      v-model="showResetModal"
      :is-disabled="loading"
      :is-loading="loading"
      @modal-confirm="onResetConfirm"
    />

    <!-- Help Modal -->
    <generic-modal v-model="showHelpModal" title="Buy Guide Help" hide-buttons hide-louis>
      <template v-for="(helpItem, index) in help">
        <div :key="helpItem.heading">
          <v-divider v-if="index !== 0" class="mb-2" />
          <div class="text-body-1 font-weight-bold">{{ helpItem.heading }}</div>
          <p>{{ helpItem.text }}</p>
        </div>
      </template>
      <div class="text-h6">Academy Docs:</div>
      <ul>
        <li>
          <a href="https://academy.investoogroup.com/wiki/managing-buy-guides-in-console/"
            >Basic Buy Guide Management</a
          >
        </li>
        <li>
          <a href="https://academy.investoogroup.com/wiki/translating-buy-guides/">Translating Buy Guides</a>
        </li>
      </ul>
    </generic-modal>

    <!-- Translate Single Modal -->
    <generic-modal
      v-model="showTranslateSingleModal"
      :is-disabled="loading || !selectedGuideLanguage.language"
      :is-loading="loading"
      title="Translate Buy Guide"
      hide-louis
      persistent
      @modal-confirm="onTranslateSingleConfirm"
      @modal-cancel="showTranslateSingleModal = false"
    >
      <template v-if="selectedGuideLanguage.language">
        <div>
          Are you sure you want to translate the content for the following Buy Guide?
        </div>
        <div>
          <span class="font-weight-bold">{{ item.name }}</span>
          <span class="text-caption"> - {{ formattedOption('buyGuideTypes', formData.type) }}</span>
        </div>
        <div class="d-inline-flex align-center">
          <flag-pip :flag="selectedGuideLanguage.language.flag" class="mr-2" />
          <div>
            <b>{{ selectedGuideLanguage.language.name }} - </b>
            <span v-if="selectedGuideLanguage.id" class="text-caption warning--text">Existing Guide</span>
            <span v-else class="text-caption success--text">New Guide</span>
          </div>
        </div>
        <div v-if="selectedGuideLanguage.id" class="mt-4">
          <v-icon left small color="warning">warning</v-icon>
          <span
            >Existing content in the <b>{{ selectedGuideLanguage.language.name }}</b> Buy Guide will be overwritten by
            the translated content.</span
          >
        </div>
      </template>
      <div v-else>There is an issue translating to the selected language.</div>
    </generic-modal>

    <!-- Translate All Modal -->
    <generic-modal
      v-model="showTranslateAllModal"
      :is-disabled="loading"
      :is-loading="loading"
      title="Translate Buy Guide for All Languages"
      hide-louis
      persistent
      @modal-confirm="onTranslateAllConfirm"
      @modal-cancel="showTranslateAllModal = false"
    >
      <div class="mb-2">
        Are you sure you want to translate the content of the English Buy Guide to all languages for the following Buy
        Guide?
      </div>
      <div class="mb-2">
        <span class="font-weight-bold">{{ item.name }}</span>
        <span class="text-caption"> - {{ formattedOption('buyGuideTypes', formData.type) }}</span>
      </div>
      <div class="mb-2">
        <v-icon left small color="warning">warning</v-icon>
        <span>Content in existing Buy Guides will be overwritten by the new translated content.</span>
      </div>
      <div v-if="loading" class="text-caption font-italic">
        Translating content... Please do not refresh or close this window.
      </div>
    </generic-modal>

    <!-- Save Modal -->
    <generic-modal
      v-model="showSaveModal"
      :is-disabled="loading"
      :is-loading="loading"
      title="Save Entity"
      persistent
      @modal-confirm="onSaveConfirm"
      @modal-cancel="showSaveModal = false"
    >
      <span>Are you sure you want to save these changes?</span>
    </generic-modal>

    <exit-modal
      :is-disabled="loading"
      :is-loading="loading"
      :value="showExitModal"
      @modal-confirm="confirmExitBeforeSave"
      @modal-cancel="cancelExitBeforeSave"
    />
  </page-loader>
</template>

<script>
// Helpers
import { cloneDeep } from 'lodash-es'
import { mapGetters, mapMutations, mapActions } from 'vuex'
import { truncate } from '@/utils/string'
import { formatLastUpdated } from '@/utils/date'
import { categoriseSchema, parseSchema } from '@/utils/parse-schema'
// Mixins
import DataModification from '@/mixins/data-modification'
import PermissionsHandler from '@/mixins/permissions-handler'
import RedirectHandler from '@/mixins/redirect-handler'
// Components
import BaseChip from '@/components/chips/base-chip'
import CategoryOptions from '@/components/category-options'
import DeleteModal from '@/components/modals/delete-modal'
import ExitModal from '@/components/modals/exit-modal'
import FabActions from '@/components/fab-actions'
import FlagPip from '@/views/news-articles/components/flag-pip'
import FroalaInput from '@/components/forms/froala-input'
import GenericModal from '@/components/modals/generic-modal'
import PageLoader from '@/components/page-loader'
import ResetModal from '@/components/modals/reset-modal'
import SelectImage from '@/components/forms/select-image'
import SelectList from '@/components/forms/select-list'
import TextInput from '@/components/forms/text-input'
import ToggleInput from '@/components/forms/toggle-input'
// Services
import { buyGuides, entities } from '@/services/offers'

const emptyBuyGuide = () => ({
  type: null,
  language_id: null,
  entity_id: null,
  entity_type_id: null,
  is_default: true,
  buy_guide: {},
})

export default {
  name: 'BuyGuidesEdit',
  components: {
    BaseChip,
    DeleteModal,
    ExitModal,
    FabActions,
    FlagPip,
    FroalaInput,
    GenericModal,
    PageLoader,
    ResetModal,
    SelectImage,
    SelectList,
    CategoryOptions,
    TextInput,
    ToggleInput,
  },
  mixins: [DataModification, PermissionsHandler, RedirectHandler],
  data() {
    return {
      help: [
        {
          heading: 'How can I delete a Buy Guide?',
          text:
            'Buy Guides can only be deleted if they meet one of the following requirements: the guide is not English or the guide is English but there are no other languages for the current Buy Guide. The reason for having these requirements is because we use the English version of a guide to group all Buy Guides in the Index view and therefore if we remove the English version, we have no access to the remaining languages. If the Buy Guide meets the requirements, you can click the FAB and click the Delete Buy Guide button.',
        },
        {
          heading: 'Translating a Buy Guide',
          text:
            'Navigate to the Languages section and then click the Translate button located in the item for the relevant language. This will open a modal for you to confirm your decision.',
        },
        {
          heading: 'Where does the translated content come from?',
          text: 'The English Buy Guide will always be the source of the content to translate from.',
        },
        {
          heading: 'What languages can be translated to?',
          text:
            'Any languages that exist in Language Management in the console can be translated to, with the exception of English, since this is the source from which the Buy Guide content will be translated.',
        },
        {
          heading: 'What happens to existing content when translating?',
          text:
            'If translating to an existing Buy Guide, the existing content will be overwritten by the newly translated content. There is a warning in the confirmation modal to remind users of this.',
        },
        {
          heading: "Why isn't there a translate button in the languages?",
          text:
            'You can only translate from the English guide therefore no buttons will appear when viewing a guide for any other language.',
        },
      ],
      loading: false,
      entities: [],
      schema: {},
      formData: emptyBuyGuide(),
      guideLanguages: [],
      newLanguageId: null,
      rules: {
        type: [v => !!v || 'Type is required'],
        language_id: [v => !!v || 'Language is required'],
        entity_id: [v => !!v || 'Entity is required'],
      },
      original: emptyBuyGuide(),
      showDeleteModal: false,
      showHelpModal: false,
      showResetModal: false,
      showSaveModal: false,
      showTranslateAllModal: false,
      showTranslateSingleModal: false,
    }
  },
  computed: {
    ...mapGetters({
      getItemsForOption: 'options/getItemsForOption',
      formattedOption: 'options/formattedOption',
      entityTypes: 'entityTypes/getEntityTypeOptions',
      languages: 'languages/getLanguages',
      languageOptions: 'languages/getLanguageOptions',
      isPageLoading: 'general/isPageLoading',
    }),
    breadcrumbs() {
      const { item } = this
      const type = this.formData.type

      return [
        { text: 'Buy Guides', to: { name: 'content.buy-guides' }, exact: true },
        {
          text: `${item.name} - ${this.formattedOption('buyGuideTypes', type)}`,
          disabled: true,
        },
      ]
    },
    canDelete() {
      if (this.isPageLoading) return false

      const hasTranslatedGuides = !!this.guideLanguages.length

      return this.isPermittedTo('delete') && (!hasTranslatedGuides || !this.isCurrentGuideEnglish)
    },
    hasChanges() {
      return this.hasDataChanged(this.original, this.formData)
    },
    isCurrentGuideEnglish() {
      return this.formData?.language?.code === 'en'
    },
    item() {
      if (this.formData.entity_id)
        return { name: this.entities.find(({ value }) => value === this.formData.entity_id)?.key }
      if (this.formData.entity_type_id) return { name: this.formData.entityType.name }
      return { name: 'Default Buy Guide' }
    },
    newLanguageOptions() {
      return this.languages.data.map(({ key, value }) => {
        const disabled =
          !!this.guideLanguages.find(({ language }) => language.id === value) || value === this.formData.language_id
        return { key, value, disabled }
      })
    },
    selectedGuideLanguage() {
      return this.guideLanguages.find(({ language }) => language.id === this.newLanguageId) || {}
    },
  },
  async created() {
    try {
      const paginate = { perPage: null }
      await Promise.all([
        this.fetchEntityTypes({ paginate }),
        this.fetchLanguages({ paginate }),
        this.fetchEntities(paginate),
      ])
      this.formData = await buyGuides.get(this.$route.params.id)
      this.original = cloneDeep(this.formData)
      await this.fetchGuideLanguages()
    } catch (error) {
      this.$notify({ type: 'error', text: 'Unable to load Buy Guide', data: { error }, duration: -1 })
      this.$_redirectHandler_404({ name: 'content.buy-guides' })
    }

    this.schema = await this.getSchema()
    this.setPageLoading(false)
    this.setBeforeUnloadListener(this.onBeforeUnload)
  },
  beforeRouteLeave(to, from, next) {
    if (!this.hasChanges) return next()
    this.handleExitBeforeSave(next)
  },
  beforeRouteUpdate(to, from, next) {
    if (!this.hasChanges) return next()
    this.setPageLoading(false)
    this.handleExitBeforeSave(next)
    return false
  },
  beforeDestroy() {
    this.removeBeforeUnloadListener(this.onBeforeUnload)
  },
  methods: {
    ...mapActions({
      fetchEntityTypes: 'entityTypes/fetch',
      fetchLanguages: 'languages/fetch',
    }),
    ...mapMutations({
      setPageLoading: 'general/setPageLoading',
    }),
    formatLastUpdated,
    truncate,
    async fetchEntities(paginate) {
      try {
        this.entities = await entities.getEntities(paginate, true)
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch entities',
          data: { error },
          duration: -1,
        })
      }
    },
    async getSchema() {
      const schema = await buyGuides.getSchema(this.formData.type)
      const parsedSchema = parseSchema(schema)
      return categoriseSchema(parsedSchema)
    },
    async fetchGuideLanguages() {
      const { entity_id, entity_type_id, type, is_default } = this.formData
      const filters = {
        entity_id,
        entity_type_id,
        type,
        entity_idNotSet: !entity_id || undefined,
        entity_type_idNotSet: !entity_type_id || undefined,
        is_default: !!is_default,
      }
      const { data } = await buyGuides.getAll({ paginate: { perPage: null }, filters })
      this.guideLanguages = this.languages.data
        .map(language => {
          const buyGuide = data.find(guide => guide.language.id === language.id)
          if (!buyGuide) return { ...emptyBuyGuide(), language, disabled: true }
          return buyGuide
        })
        .sort((a, b) => a.language.name.localeCompare(b.language.name))
      try {
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch translated buy guides',
          data: { error },
          duration: -1,
        })
      }
    },
    hasType(type) {
      if (typeof type === 'string') return this.formData.type === type
      return type.includes(this.formData.type)
    },
    async onDeleteConfirm() {
      await buyGuides.delete(this.$route.params.id)
      this.$router.push({ name: 'content.buy-guides' })
    },
    async onResetConfirm() {
      this.formData = cloneDeep(this.original)
      this.showResetModal = false
    },
    async onTranslateSingleConfirm() {
      this.loading = true

      const { id } = await buyGuides.translate(this.formData.id, { language_id: this.newLanguageId })
      this.$notify({
        type: 'success',
        text: `Buy Guide successfully translated to ${this.selectedGuideLanguage.language.name}`,
        data: {
          route: { name: 'content.buy-guides.edit', params: { id } },
        },
        duration: 7000,
      })
      this.newLanguageId = null
      this.showTranslateSingleModal = false
      try {
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to translate Buy Guide',
          data: { error },
          duration: -1,
        })
      }
      this.loading = false
    },
    async onTranslateAllConfirm() {
      this.loading = true

      try {
        await buyGuides.translate(
          this.formData.id,
          this.languageOptions.map(({ value }) => ({ language_id: value }))
        )
        this.$notify({ type: 'success', text: 'Buy Guide languages successfully translated' })
        this.showTranslateAllModal = false

        await this.fetchGuideLanguages()
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to translate languages for Buy Guide',
          data: { error },
          duration: -1,
        })
      }
      this.loading = false
    },
    onInput({ context, model, value }) {
      if (context) {
        if (this.formData[model]) {
          this.$set(this.formData[model], context, value)
        } else {
          this.$set(this.formData, model, { [context]: value })
        }
      } else this.$set(this.formData, model, value)
    },
    async onSaveConfirm() {
      this.loading = true

      if (!this.$refs.form.validate()) {
        this.showSaveModal = false
        this.loading = false
        return
      }

      try {
        this.formData = await buyGuides.update(this.formData.id, this.formData)
        this.original = cloneDeep(this.formData)
        this.$notify({ type: 'success', text: 'The buy guide was successfully saved.' })
      } catch (error) {
        this.$notify({ type: 'error', text: 'There was an error saving this buy guide', data: { error }, duration: -1 })
      }
      this.showSaveModal = false
      this.loading = false
    },
    onBeforeUnload(event) {
      if (!this.hasChanges) return
      this.confirmUnload(event)
    },
    onTranslateButtonClick(guide) {
      if (!guide.language.id) {
        this.$notify({ type: 'error', text: 'Cannot translate from this language' })
      }

      this.newLanguageId = guide.language.id
      this.showTranslateSingleModal = true
    },
  },
}
</script>
